import { PushNotifications } from '@capacitor/push-notifications';
import { FCM } from '@capacitor-community/fcm';
import { fbConfig, firebaseApp } from 'boot/firebase';
import { i18n } from 'boot/i18n';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { get, omit } from 'lodash';
import { Dialog, Platform } from 'quasar';
import { useAuth } from 'src/composables/auth';
import { useTime } from 'src/composables/time';
import { CANCELLED_APPOINTMENT_DESTINATIONS, NOTIFICATION_ENTRY_POINTS } from 'src/constants/analytics';
import NOTIFICATION_TYPES from 'src/constants/notifications';
import AddendumsDB from 'src/fb/addendums-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionsDB from 'src/fb/sessions-db';
import UserNotificationsDB from 'src/fb/userNotifications-db';
import { notificationAnalytics } from 'src/services/analytics';
import { auth as useAuthStore } from 'src/store/auth';
import { notifications as useNotificationsStore } from 'src/store/notifications';
import { formatAppointmentSchedule } from 'src/utils/DateUtils';
import { logEvent } from 'src/utils/LogEvent';
import { computed } from 'vue';
import { isNavigationFailure, useRouter } from 'vue-router';

import { notifyInfo, notifyWarning } from './interactions';

/**
 * Where a cancelled-appointment notification should route its recipient, shared by the router
 * push in `goToNotification` and the `navigated`/`destination` analytics properties in
 * `handleNotificationClick` so the two never disagree.
 *
 * @param {(privilege: string) => boolean} userHasPrivilege
 * @returns {string|null}
 */
function getCancelledAppointmentDestination(userHasPrivilege) {
  if (userHasPrivilege('pages/mySchedule')) {
    return CANCELLED_APPOINTMENT_DESTINATIONS.MY_SCHEDULE;
  }

  if (userHasPrivilege('pages/scheduling')) {
    return CANCELLED_APPOINTMENT_DESTINATIONS.SCHEDULING;
  }

  return null;
}

export const ACTION_NOT_AVAILABLE_REASONS = {
  NON_ACTION: 'non-action',
  RESOURCE_DELETED: 'resource-deleted',
  ACTION_COMPLETED: 'action-completed',
  UNKNOWN: 'unknown',
};

const NOTIFICATION_TYPES_TO_DISABLE_REQUIRED_ACTIONS_PRIVILEGES = {
  [NOTIFICATION_TYPES.PENDING_SIGNATURES]: 'disableRequiredActions/pendingSignatures',
  [NOTIFICATION_TYPES.PENDING_NOTES]: 'disableRequiredActions/pendingNotes',
  [NOTIFICATION_TYPES.PENDING_ACTIVITIES]: 'disableRequiredActions/pendingActivities',
  [NOTIFICATION_TYPES.PENDING_SESSION]: 'disableRequiredActions/pendingSession',
  [NOTIFICATION_TYPES.PENDING_REPORT_REVIEW]: 'disableRequiredActions/pendingReportReview',
};

let updateCountTimoutId = null;

// eslint-disable-next-line import/prefer-default-export
export function useNotifications() {
  const router = useRouter();
  const auth = useAuth();
  const { formatDate } = useTime();
  const authStore = useAuthStore();
  const notificationsStore = useNotificationsStore();

  const userId = computed(() => get(authStore, ['userDetails', 'userId'], null));

  const disabledRequiredActions = computed(() => {
    return Object.entries(NOTIFICATION_TYPES_TO_DISABLE_REQUIRED_ACTIONS_PRIVILEGES)
      .reduce((acc, [type, privilege]) => {
        if (auth.userHasPrivilege(privilege)) {
          acc.push(type);
        }

        return acc;
      }, []);
  });

  const userNotificationsDb = new UserNotificationsDB();

  /**
   * Sometimes, due to incorrect data deletion or syncing, we may encounter a situation where
   * an action can't be completed anymore, but a pending action notification still exists.
   * This function is used as a workaround to check if the action is still available
   */
  async function isNotificationActionAvailable(notification) {
    try {
      const { context, session, type } = notification;

      const actionNotifications = [
        NOTIFICATION_TYPES.PENDING_SESSION,
        NOTIFICATION_TYPES.PENDING_SIGNATURES,
        NOTIFICATION_TYPES.PENDING_NOTES,
        NOTIFICATION_TYPES.PENDING_ACTIVITIES,
        NOTIFICATION_TYPES.SESSION_ACTIVITY_CHANGE_REQUEST_REJECTED,
      ];

      if (!actionNotifications.includes(type)) {
        return { available: false, reason: ACTION_NOT_AVAILABLE_REASONS.NON_ACTION };
      }

      if (type === NOTIFICATION_TYPES.PENDING_SESSION) {
        const sessionsDb = new SessionsDB();
        const foundSession = await sessionsDb.read(session);

        if (!foundSession || !!foundSession?.isDeleted) {
          return { available: false, reason: ACTION_NOT_AVAILABLE_REASONS.RESOURCE_DELETED };
        }

        // We can only finish the session is still running
        if (foundSession.status !== 'running') {
          return { available: false, reason: ACTION_NOT_AVAILABLE_REASONS.ACTION_COMPLETED };
        }

        return { available: true };
      }

      const sessionActivitiesDb = new SessionActivitiesDB();
      const foundSessionActivity = await sessionActivitiesDb.read(context.sessionActivityId);

      if (!foundSessionActivity || !!foundSessionActivity?.isDeleted) {
        return { available: false, reason: ACTION_NOT_AVAILABLE_REASONS.RESOURCE_DELETED };
      }

      switch (type) {
        case NOTIFICATION_TYPES.PENDING_SIGNATURES: {
          // Addendum signature requests point at a signatory on the addendum, not on the activity
          let signatureOwner = foundSessionActivity;

          if (context.addendumId) {
            const addendum = await new AddendumsDB(context.sessionActivityId).read(context.addendumId);

            if (!addendum || addendum.isDeleted) {
              return { available: false, reason: ACTION_NOT_AVAILABLE_REASONS.RESOURCE_DELETED };
            }

            signatureOwner = addendum;
          }

          const foundSignatory = signatureOwner.requiredSignatories?.find(
            ({ uid }) => uid === context.signatoryId,
          );

          // We can only sign a signature if it's still required
          if (!foundSignatory) {
            return { available: false, reason: ACTION_NOT_AVAILABLE_REASONS.ACTION_COMPLETED };
          }

          return { available: true };
        }
        case NOTIFICATION_TYPES.PENDING_NOTES: {
          // We can complete the notes only if the session activity is in finished state
          if (foundSessionActivity.status !== 'finished') {
            return { available: false, reason: ACTION_NOT_AVAILABLE_REASONS.ACTION_COMPLETED };
          }

          return { available: true };
        }
        case NOTIFICATION_TYPES.PENDING_ACTIVITIES: {
          // We can render the activity only if it's in closed state
          if (foundSessionActivity.status !== 'closed') {
            return { available: false, reason: ACTION_NOT_AVAILABLE_REASONS.ACTION_COMPLETED };
          }

          return { available: true };
        }
        case NOTIFICATION_TYPES.SESSION_ACTIVITY_CHANGE_REQUEST_REJECTED: {
          if (['approved', 'pending'].includes(foundSessionActivity.changeRequest.status)) {
            return { available: false, reason: ACTION_NOT_AVAILABLE_REASONS.ACTION_COMPLETED };
          }

          return { available: true };
        }
        default: {
          return { available: false, reason: ACTION_NOT_AVAILABLE_REASONS.NON_ACTION };
        }
      }
    } catch (err) {
      // In most cases, this error will be thrown when the resource is unavailable for current user
      return { available: false, reason: ACTION_NOT_AVAILABLE_REASONS.UNKNOWN };
    }
  }

  function handleCounterReloading() {
    clearTimeout(updateCountTimoutId);

    updateCountTimoutId = setTimeout(() => {
      notificationsStore.loadNewNotificationsCount(
        {
          userId: userId.value,
          disabledRequiredActions: disabledRequiredActions.value,
        },
      );
    }, 500);
  }

  async function goToNotification(notification) {
    const { type, client, session, context } = notification;

    switch (type) {
      case NOTIFICATION_TYPES.PENDING_SIGNATURES:
      case NOTIFICATION_TYPES.SESSION_FINISHED:
      case NOTIFICATION_TYPES.SESSION_FILES_ATTACHED:
      case NOTIFICATION_TYPES.TARGET_REOPENED:
      case NOTIFICATION_TYPES.FILE_UPLOAD_FAILED: {
        if (context.resourceType === 'program') {
          return router.push({
            name: 'Edit Program',
            params: { programId: context.programId },
            query: { client },
          });
        }

        // The session page expands an activity on its own only when its note lists the user as a
        // pending signatory. An addendum signer is listed on the addendum, so name the activity.
        if (context.addendumId) {
          if (session) {
            return router.push({
              name: 'History View - Activity',
              params: { sessionId: session, sessionActivityId: context.sessionActivityId },
              query: { client },
            });
          }

          return router.push({
            name: 'Activities',
            query: { resetClient: true, activity: context.sessionActivityId },
          });
        }

        return router.push({
          name: 'History View',
          params: { sessionId: session },
          query: { client },
        });
      }
      case NOTIFICATION_TYPES.PENDING_REPORT_REVIEW:
      case NOTIFICATION_TYPES.COMMENTS: {
        if (context.progressReportId) {
          return router.push({
            name: 'Progress Report',
            params: { progressReportId: context.progressReportId },
            query: { client, thread: context.thread },
          });
        }

        return router.push({
          name: 'History View',
          params: { sessionId: session },
          query: { client },
        });
      }
      case NOTIFICATION_TYPES.PENDING_NOTES:
      case NOTIFICATION_TYPES.PENDING_ACTIVITIES:
      case NOTIFICATION_TYPES.SESSION_ACTIVITY_CHANGE_REQUEST_REJECTED: {
        if (session) {
          return router.push({
            name: 'History View',
            params: { sessionId: session },
            query: { client },
          });
        }

        return router.push({
          name: 'Activities',
          query: { resetClient: true, activity: context.sessionActivityId },
        });
      }
      case NOTIFICATION_TYPES.MISSED_APPOINTMENT: {
        if (session) {
          return router.push({
            name: context.isGroupSession ? 'Prepare Group Session' : 'Session Overview',
            params: { sessionId: session },
            query: { client, appointment: context.appointmentId },
          });
        }

        if (client) {
          return router.push({
            name: 'Client Appointments',
            query: { client },
          });
        }

        return router.push({
          name: 'My Schedule',
          query: { resetClient: true },
        });
      }
      case NOTIFICATION_TYPES.TARGET_MASTERY: {
        return router.push({
          name: 'Progress',
          params: { programId: context.programId },
          query: { client },
        });
      }
      case NOTIFICATION_TYPES.PENDING_SESSION: {
        if (context.sessionType === 'form') {
          return router.push({
            name: 'Home',
            query: { client, sessionId: session, finish: true },
          });
        }

        return router.push({
          name: 'session-data-collection',
          params: { sessionId: session },
          query: { client, finish: true },
        });
      }
      case NOTIFICATION_TYPES.PROGRAM_MODIFICATION: {
        return router.push({
          name: 'Home',
          query: { client, program: context.programId, tab: 'programMods' },
        });
      }
      case NOTIFICATION_TYPES.VIDEO_SESSION_INVITATION: {
        return router.push({
          name: 'session-data-collection',
          params: { sessionId: session },
          query: { client, join_call: true },
        });
      }
      case NOTIFICATION_TYPES.SESSION_REMINDER: {
        return router.push({
          name: 'Home',
          query: { client },
        });
      }
      // A restore routes exactly like a cancellation: same appointment, same date.
      case NOTIFICATION_TYPES.RESTORED_APPOINTMENT:
      case NOTIFICATION_TYPES.CANCELLED_APPOINTMENT: {
        // Without this, My Schedule always opens on today - a cancellation for another day still
        // leaves the recipient hunting for it. AppointmentsSchedule.vue's
        // initPeriod() reads this as its opening date instead of `new Date()` when present.
        const scheduledDate = context?.scheduledStartTimestamp instanceof Timestamp
          ? formatDate(context.scheduledStartTimestamp, 'yyyy-MM-dd')
          : null;

        const destination = getCancelledAppointmentDestination(auth.userHasPrivilege);

        if (destination === CANCELLED_APPOINTMENT_DESTINATIONS.MY_SCHEDULE) {
          return router.push({
            name: 'My Schedule',
            query: { resetClient: true, ...(scheduledDate ? { date: scheduledDate } : {}) },
          });
        }

        // Administrators default to `pages/mySchedule: false`, so the router would bounce them to
        // '/' - fall back to the general scheduling page instead. Unlike My Schedule, which is the
        // reader's own calendar and so resets the client, this one is clinic-wide: it gets the
        // cancelled appointment's own client too, so it opens on that client's schedule rather
        // than on whoever happened to be selected last. Both params are read by initFromRouteQuery().
        if (destination === CANCELLED_APPOINTMENT_DESTINATIONS.SCHEDULING) {
          return router.push({
            name: 'Scheduling',
            query: {
              ...(scheduledDate ? { date: scheduledDate } : {}),
              ...(client ? { client } : {}),
            },
          });
        }

        return undefined;
      }
      default: {
        break;
      }
    }
  }

  async function safeUpdate(id, data = {}) {
    try {
      return await userNotificationsDb.update({ id, ...data });
    } catch (err) {
      // Ignore only missing-doc errors; report others
      if (err?.message?.includes('No document to update')) {
        return;
      }

      throw err;
    }
  }

  async function setViewed(notificationId, viewed) {
    await safeUpdate(notificationId, { viewed });
    handleCounterReloading();
  }

  async function handleNotificationClick(notification, entryPoint = NOTIFICATION_ENTRY_POINTS.UNKNOWN) {
    try {
      // @todo: investigate if we need to call this function after all checks
      //  and whether we should allow navigating through already completed action notifications
      // A navigation guard can throw, rejecting router.push() instead of resolving with a failure -
      // caught here so the opened event still records navigated: false, then rethrown so every other
      // notification type keeps today's behavior of skipping straight to the outer catch below.
      let navigationResult;
      let navigationError;

      try {
        navigationResult = await goToNotification(notification);
      } catch (err) {
        navigationError = err;
      }

      if (notification.type === NOTIFICATION_TYPES.CANCELLED_APPOINTMENT) {
        const destination = getCancelledAppointmentDestination(auth.userHasPrivilege);
        const navigated = !navigationError && destination !== null && !isNavigationFailure(navigationResult);

        notificationAnalytics.trackNotificationOpened(notification, entryPoint, { navigated, destination });
      }

      if (navigationError) {
        throw navigationError;
      }

      const { available, reason } = await isNotificationActionAvailable(notification);

      if (available || (!available && reason === ACTION_NOT_AVAILABLE_REASONS.NON_ACTION)) {
        if (!notification.viewed) {
          await setViewed(notification.id, true);
        }

        return;
      }

      if ([ACTION_NOT_AVAILABLE_REASONS.RESOURCE_DELETED, ACTION_NOT_AVAILABLE_REASONS.UNKNOWN].includes(reason)) {
        await userNotificationsDb.delete(notification.id);

        notifyWarning(i18n.t('notifications.actionNotAvailable'));
        return;
      }

      if (reason === ACTION_NOT_AVAILABLE_REASONS.ACTION_COMPLETED) {
        if (notification.actionCompleted) {
          return;
        }

        await safeUpdate(notification.id, {
          actionCompleted: true,
          actionCompletedTimestamp: serverTimestamp(),
          viewed: true,
        });

        notifyWarning(i18n.t('notifications.actionNotAvailable'));
      }
    } catch (err) {
      console.error(err);
      logEvent(err, {
        context: {
          context_data: { notification },
        },
      });
    }
  }

  function mapPushNotificationTypeToNotificationType(pushNotificationType) {
    // We have to use outdated notification types to keep compatibility with the old version of the app
    switch (pushNotificationType) {
      case 'signatureRequest': {
        return NOTIFICATION_TYPES.PENDING_SIGNATURES;
      }
      case 'failedUpload': {
        return NOTIFICATION_TYPES.FILE_UPLOAD_FAILED;
      }
      default: {
        return pushNotificationType;
      }
    }
  }

  async function setupNativePushNotifications() {
    try {
      const { receive: checkPermissionsResult } = await PushNotifications.checkPermissions();

      if (checkPermissionsResult !== 'granted') {
        const { receive: requestPermissionsResult } = await PushNotifications.requestPermissions();

        if (requestPermissionsResult !== 'granted') {
          return;
        }
      }

      PushNotifications.addListener('registration', async (token) => {
        try {
          if (!Platform.is.ios) {
            return authStore.updateMessagingToken({ value: token.value, type: 'native' });
          }

          const iosToken = await FCM.getToken();
          await authStore.updateMessagingToken({ value: iosToken.token, type: 'native' });
        } catch (err) {
          logEvent(err);
        }
      });

      PushNotifications.addListener('registrationError', (err) => logEvent(err));

      await PushNotifications.register();
    } catch (err) {
      logEvent(err);
    }
  }

  async function setupWebPushNotifications() {
    try {
      const isBrowserSupported = await isSupported();

      if (!isBrowserSupported) {
        console.warn('FCM doesn\'t support this browser');
        return;
      }

      const messaging = getMessaging(firebaseApp);

      const token = await getToken(messaging, { vapidKey: fbConfig.FIREBASE_FCM_VAPID_KEY });

      await authStore.updateMessagingToken({ value: token, type: 'web' });
    } catch (err) {
      if (err.code !== 'messaging/permission-blocked') {
        console.error('An error occurred while retrieving token', err.message);
        logEvent(err);
      }
    }
  }

  function createToastNotification(notification, callback) {
    const { type, context } = notification;

    // The restore toast names the appointment instead of the product: it is the first surface an
    // active staff member sees, so it has to answer "which appointment?" on its own.
    const params = type === NOTIFICATION_TYPES.RESTORED_APPOINTMENT
      && context?.scheduledStartTimestamp instanceof Timestamp
      ? {
        ...context,
        scheduledDate: formatAppointmentSchedule(context.scheduledStartTimestamp.toDate()),
      }
      : context;

    return notifyInfo(i18n.t(`notifications.${type}.title`, params), {
      timeout: 5000,
      actions: [
        {
          label: i18n.t(`notifications.${type}.action`),
          handler: callback,
        },
      ],
    });
  }

  function createConfirmDialogNotification(notification, callback) {
    const { type, context } = notification;

    return Dialog.create({
      title: i18n.t(`notifications.${type}.title`),
      message: i18n.t(`notifications.${type}.message`, { userName: context.senderUserName }),
      cancel: true,
      persistent: true,
    })
      .onOk(callback);
  }

  function handleInAppNotificationReceived(payload) {
    if (router.currentRoute.value?.name === 'session-data-collection') {
      return;
    }

    const baseNotification = {
      id: payload.payload.notificationId,
      clinic: payload.clinic,
      client: payload.client,
      session: payload.session,
      user: payload.user,
      type: payload.type,
      context: omit(payload.payload, 'notificationId'),
    };

    const callback = async () => handleNotificationClick(baseNotification, NOTIFICATION_ENTRY_POINTS.TOAST);

    switch (baseNotification.type) {
      case NOTIFICATION_TYPES.COMMENTS:
      case NOTIFICATION_TYPES.PENDING_SIGNATURES:
      case NOTIFICATION_TYPES.PENDING_NOTES:
      case NOTIFICATION_TYPES.PENDING_ACTIVITIES:
      case NOTIFICATION_TYPES.MISSED_APPOINTMENT:
      case NOTIFICATION_TYPES.TARGET_MASTERY:
      case NOTIFICATION_TYPES.PENDING_SESSION:
      case NOTIFICATION_TYPES.PROGRAM_MODIFICATION:
      case NOTIFICATION_TYPES.SESSION_FINISHED:
      case NOTIFICATION_TYPES.SESSION_FILES_ATTACHED:
      case NOTIFICATION_TYPES.TARGET_REOPENED:
      case NOTIFICATION_TYPES.FILE_UPLOAD_FAILED:
      case NOTIFICATION_TYPES.SESSION_REMINDER: {
        createToastNotification(baseNotification, callback);
        break;
      }
      case NOTIFICATION_TYPES.CANCELLED_APPOINTMENT:
      case NOTIFICATION_TYPES.RESTORED_APPOINTMENT: {
        notificationAnalytics.trackNotificationShown(baseNotification, NOTIFICATION_ENTRY_POINTS.TOAST);
        createToastNotification(baseNotification, callback);
        break;
      }
      case NOTIFICATION_TYPES.VIDEO_SESSION_INVITATION: {
        createConfirmDialogNotification(baseNotification, callback);
        break;
      }
      default: {
        break;
      }
    }
  }

  async function handlePushNotificationPerformed(payload) {
    const { notification: { data: { type, clientId, sessionId, recipientId, notificationId, ...context } } } = payload;

    if (recipientId !== userId.value) {
      return;
    }

    const formattedType = mapPushNotificationTypeToNotificationType(type);

    const baseNotification = {
      type: formattedType,
      client: clientId,
      session: sessionId,
      user: recipientId,
      context,
    };

    // In case when we don't create a real notification in the DB, we should just navigate to the appropriate page
    if (!notificationId) {
      return goToNotification(baseNotification);
    }

    await handleNotificationClick({ id: notificationId, ...baseNotification }, NOTIFICATION_ENTRY_POINTS.PUSH);
  }

  async function setupPushNotifications() {
    try {
      if (process.env.MODE === 'capacitor') {
        await setupNativePushNotifications();
        PushNotifications.addListener('pushNotificationActionPerformed', handlePushNotificationPerformed);
        return;
      }

      await setupWebPushNotifications();
    } catch (err) {
      logEvent(err);
    }
  }

  return {
    goToNotification,
    setViewed,
    setupPushNotifications,
    handleInAppNotificationReceived,
    handleNotificationClick,
    isNotificationActionAvailable,
    handleCounterReloading,
    disabledRequiredActions,
  };
}
